import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  DatasourceDownstreamSheetsQuery,
  DatasourceDownstreamSheetsQueryVariables,
} from '../../../gen-src/queries/DatasourceDownstreamSheetsQuery';
import {
  DownstreamSheetsFilteredByFieldQuery,
  DownstreamSheetsFilteredByFieldQuery_fieldsConnection_nodes,
  DownstreamSheetsFilteredByFieldQueryVariables,
} from '../../../gen-src/queries/DownstreamSheetsFilteredByFieldQuery';
import { SheetsDisplayGridRowDataFragment } from '../../../gen-src/queries/SheetsDisplayGridRowDataFragment';
import { SheetSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatasourceDownstreamSheetsQueryGql } from '../../queries/DatasourceDownstreamSheetsQueryGql';
import { DownstreamSheetsFilteredByFieldQueryGql } from '../../queries/DownstreamSheetsFilteredByFieldQueryGql';
import { getRowDataForSheet, SheetsDisplayGrid, SheetsDisplayGridRowData } from '../sharedDisplayGrid/SheetsDisplayGrid';
import { SheetsValidSortFields } from '../sorting/sortProviders/SheetsGridSortProvider';

type QueryVariables = DatasourceDownstreamSheetsQueryVariables;
type QueryResults = DatasourceDownstreamSheetsQuery;
type QueryResultListNode = SheetsDisplayGridRowDataFragment;
type GridRowData = SheetsDisplayGridRowData;

type QueryVariablesWithFilter = DownstreamSheetsFilteredByFieldQueryVariables;
type QueryResultsWithFilter = DownstreamSheetsFilteredByFieldQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0] &&
    resultData.publishedDatasources[0].downstreamSheetsConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter):
  ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && getSheetsConnection(resultData.fieldsConnection.nodes[0]);
}

function getSheetsConnection(field: DownstreamSheetsFilteredByFieldQuery_fieldsConnection_nodes) {
  return 'downstreamSheetsConnection' in field ? field.downstreamSheetsConnection : null;
}

export type DatasourceDownstreamSheetsQueryGridProps =
  GridSortProps<SheetsValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredFieldId: string | null;
    readonly rowCount: number | null;
  };

export function DatasourceDownstreamSheetsQueryGrid(props: DatasourceDownstreamSheetsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamSheetsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredSheetsLineageGridMessage();

  return props.filteredFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={DownstreamSheetsFilteredByFieldQueryGql}
      partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as SheetSortOrder }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForSheet}
    >
      {({ rowStore }) => <SheetsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatasourceDownstreamSheetsQueryGql}
        partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as SheetSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForSheet}
      >
        {({ rowStore }) => <SheetsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    );
}
